import React, { memo, useMemo, useRef } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvTypography from "@directv-digital/dds.ui.dtv-typography";
import { CheckIcon } from "@directv-digital/fusion.atoms.icons";
import { DropdownListStyles } from "./DropdownList.styles";
import { useLocationHref } from "src/hooks/useLocationHref";

{ /* Generated by Copilot- Start - 2026-02-23 16:46:18 */ }
export interface DropdownListProps {
  showDrawer: boolean;
  showDrawerNoMatch: boolean;
  filteredData: any[];
  categoryData: any;
  searchText: string;
  selectedChannels: any[];
  notifyOnSelect?: (channel: any) => void;
  notifyOnSelectAll?: (channels: any[]) => void;
  notifyOnCloseDropdown?: () => void;
  sortOptions?: boolean;
  isLoading?: boolean;
}
const DropdownList = (props: DropdownListProps) => {
  const listRef = useRef<HTMLUListElement | null>(null);
  const { showDrawer, showDrawerNoMatch, filteredData, categoryData, searchText, selectedChannels, sortOptions = true, isLoading = false } = props;
  const { typingMsg, noMatchListMsg, searchMsg } = categoryData || {};
  const styles = DropdownListStyles({
    showDrawer,
    showDrawerNoMatch
  });
  const url = useLocationHref();
  const handleKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, index: number, channel: any) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      if (typeof channel === 'object') {
        props?.notifyOnSelect?.(channel);
      } else {
        props?.notifyOnSelectAll?.(filteredData);
      }
      return;
    }
    if (event.key === "Escape") {
      props?.notifyOnCloseDropdown();
      return;
    }
    if (!listRef.current) {
      return;
    }
    const items = Array.from(
      listRef.current.querySelectorAll<HTMLLIElement>("li[tabindex='0']")
    );
    if (!items.length) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const nextIndex = (index + 1) % items.length;
      items[nextIndex]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const prevIndex = (index - 1 + items.length) % items.length;
      items[prevIndex]?.focus();
    }
  };
  const selectedChannelsData = useMemo(() => selectedChannels.map((ch: any) => ch.displayText.toLowerCase()).join(', '), [selectedChannels]);
  return (
    <DtvBox
      sx={{
        ...styles.root
      }}
    >
      <DtvBox
        sx={{
          ...styles.container
        }}
      >
        <ul
          ref={listRef}
          role="menu"
          style={{
            ...styles.menuUi
          }}
        >
        {/* Loading state — shown while the OV API call is in-flight.
        Suppresses results and no-match rows so the user sees feedback
        instead of a blank or stale dropdown during the request. */}
        {isLoading && !sortOptions && searchText?.length > 1 && (
          <li
            aria-busy="true"
            aria-live="polite"
            key="loadingText"
            role="menuitem"
            tabIndex={-1}
            style={{
              ...styles.menuli
            }}
          >
            <DtvTypography component="span" sx={{
 ...styles.menuP,
fontSize: "16px !important",
fontStyle: "italic"
}}>
              {searchMsg || "Searching…"}
            </DtvTypography>
          </li>
        )}
        {showDrawerNoMatch && (
          <li
            key={'helperText'}
            role="menuitem"
            tabIndex={0}
            title={searchText?.length === 1 ? typingMsg : noMatchListMsg}
            style={{
              ...styles.menuli
            }}
          >
            <DtvTypography component="span" sx={{
 ...styles.menuP,
fontSize: "16px !important"
}}>
            {searchText?.length === 1 ? typingMsg : noMatchListMsg }
            </DtvTypography>
          </li>
        )}
        {!isLoading && searchText?.length > 1 && (<>
          {filteredData.length > 0 && (<li
            aria-label={`${categoryData?.multiSelectTitle?.toLowerCase?.()}-${searchText?.toLowerCase?.()}`}
            data-linkdestination={url}
            data-linkposition={"panel_channel lineup"}
            id={`${categoryData?.multiSelectTitle}-${searchText}`}
            key={0}
            role="menuitem"
            tabIndex={0}
            style={{
              ...styles.menuli
            }}
            onClick={() => props?.notifyOnSelectAll?.(filteredData)}
            onKeyDown={event => handleKeyDown(event, 0, 'all')}
          >
            <DtvBox
              component="span"
              sx={{
                ...styles.menuP
              }}
            >
              {categoryData?.multiSelectTitle} <b>{searchText}</b>
            </DtvBox>
          </li>)}
          {filteredData.length > 0 && <DtvTypography component="h6" sx={{
 margin: "10px 10px",
fontWeight: 600
}}>
            {categoryData?.title}
          </DtvTypography>}
          {filteredData.map((channel: any, ix: number) => {
            const isSelected: boolean = selectedChannels.some(
              (item: any) => item.displayText === channel.displayText
            );
            const displayText: string = channel?.displayText || "";
            const searchText: string = props.searchText || "";
            const searchIndex = searchText ?
              displayText.toLowerCase().indexOf(searchText.toLowerCase()) :
              -1;

            const beforeMatch =
              searchIndex >= 0 ? displayText.slice(0, searchIndex) : displayText;
            const matchText =
              searchIndex >= 0 ?
                displayText.slice(searchIndex, searchIndex + searchText.length) :
              "";
            const afterMatch =
              searchIndex >= 0 ?
                displayText.slice(searchIndex + searchText.length) :
              "";

            return (
              // eslint-disable-next-line jsx-a11y/role-supports-aria-props
              <li
                aria-label={`search - ${displayText?.toLowerCase?.()}`}
                // eslint-disable-next-line jsx-a11y/aria-props
                aria-linklabel={`search - ${selectedChannelsData}`}
                aria-selected={isSelected}
                data-linkdestination={url}
                data-linkposition={"panel_channel lineup"}
                            id={displayText}
                key={`${ix + 1}`}
                role="menuitem"
                tabIndex={0}
                title={isSelected ? `${displayText} (is selected)` : displayText}
                style={{
                  ...styles.menuli,
                  ...isSelected && {
                    backgroundColor: "#1026411f"
                  }
                }}
                onClick={() => props?.notifyOnSelect?.(channel)}
                onKeyDown={event => handleKeyDown(event, ix + 1, channel)}
              >
                <DtvBox
                  component={"div"}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%"
                  }}
                >
                  {isSelected && <CheckIcon fill="#121212" style={{ height: "20px" }} />}
                  <DtvBox
                    component="span"
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "row",
                      gap: "2px"
                    }}
                  >
                    <DtvBox
                      component="span"
                      sx={{
                        ...styles.menuP,
                        color: isSelected ? "#121212 !important" : "#102641 !important"
                      }}
                    >
                      {beforeMatch}
                      {matchText && (
                        <span style={{ fontWeight: 600 }}>{matchText}</span>
                      )}
                      {afterMatch}
                    </DtvBox>
                    {(!sortOptions && (channel?.category || channel?.genre)) && (
                      <DtvTypography
                        component="span"
                        sx={{
                          ...styles.menuP,
                          fontSize: "12px !important",
                          color: "#888 !important",
                          fontWeight: 400
                        }}
                      >
                         - {channel?.category && channel?.genre ?
                          `${channel.category} | ${channel.genre}` :
                          channel?.category || channel?.genre
                        }
                      </DtvTypography>
                    )}
                  </DtvBox>
                </DtvBox>
              </li>
            );
          })}</>)}
        </ul>
      </DtvBox>
    </DtvBox>
  );
};

export const MemoizedDropdownList = memo(DropdownList);
{ /* Generated by Copilot- End - 2026-02-23 16:46:18 */ }
